import { useId } from 'react';

/**
 * FE-2-13 — the In-Charge gauge: a 180° arc with a blue→amber gradient and a needle
 * at the value.
 *
 * Two deliberate decisions:
 *
 * 1. **The needle renders behind the numeral.** In the mockup the needle crosses the
 *    "63%" and clips its digits. DOM order here puts the needle first and the label
 *    last, with a surface-coloured backing plate between them, so the number always
 *    stays readable whatever the value.
 *
 * 2. **Hand-rolled SVG rather than Recharts.** RadialBarChart cannot draw a needle,
 *    and layering it on top would put the needle above the label — reintroducing the
 *    exact defect this is meant to fix.
 *
 * The arc is a *polar* scale, not a sequential one: the low end means In-Control and
 * the high end In-Charge. Blue and amber are the two poles, which is why this is not
 * a single-hue ramp.
 */

const WIDTH = 320;
const HEIGHT = 190;
const CX = WIDTH / 2;
const CY = 158;
const RADIUS = 128;
const TRACK_WIDTH = 16;

/**
 * Geometry is kept in degrees, with one explicit conversion from percentage.
 *
 * An earlier version mixed the two units — it computed the needle's base as
 * `value ± 90` *percent*, which is 162° apart rather than 90°, collapsing the needle
 * into a sliver offset from the hub. Keeping percent at the boundary and degrees
 * everywhere inside makes that class of mistake impossible.
 */

/** 0% sits at 180° (left), 100% at 0° (right). */
const degreesForPct = (pct: number): number => 180 - (pct / 100) * 180;

function polar(angleDeg: number, radius: number): { x: number; y: number } {
  const radians = (angleDeg * Math.PI) / 180;
  // y is negated because SVG's y axis points down.
  return { x: CX + radius * Math.cos(radians), y: CY - radius * Math.sin(radians) };
}

function arcPath(fromPct: number, toPct: number, radius: number): string {
  const start = polar(degreesForPct(fromPct), radius);
  const end = polar(degreesForPct(toPct), radius);

  // This is a 180° dial, so no sweep between two points on it can exceed 180° and
  // the large-arc flag is always 0. Setting it to 1 makes SVG take the long way
  // round the circle, which draws the arc back under the baseline.
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`;
}

export function GaugeChart({
  value,
  label,
  bandLabel,
  bandLow,
  bandHigh,
}: {
  /** Null when there is nothing scored yet — the gauge renders empty rather than 0%. */
  value: number | null;
  label: string;
  bandLabel: string | null;
  bandLow: number;
  bandHigh: number;
}) {
  const gradientId = useId();
  const hasValue = value !== null;
  const clamped = hasValue ? Math.max(0, Math.min(100, value)) : 0;

  const needleAngle = degreesForPct(clamped);
  const needleTip = polar(needleAngle, RADIUS - TRACK_WIDTH - 10);
  // Perpendicular to the needle, in degrees, so the base is a real 14px-wide edge.
  const needleBaseA = polar(needleAngle - 90, 7);
  const needleBaseB = polar(needleAngle + 90, 7);

  return (
    <figure className="m-0 flex flex-col items-center">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full max-w-[320px]"
        role="img"
        aria-label={
          hasValue
            ? `${label}: ${clamped}%${bandLabel ? `, ${bandLabel}` : ''}`
            : 'No scored check-ins yet'
        }
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--chart-blue)" />
            <stop offset="55%" stopColor="#9fb98f" />
            <stop offset="100%" stopColor="var(--chart-amber)" />
          </linearGradient>
        </defs>

        {/* Recessive track for the full range. */}
        <path
          d={arcPath(0, 100, RADIUS)}
          fill="none"
          stroke="var(--border)"
          strokeWidth={TRACK_WIDTH}
          strokeLinecap="round"
        />

        {/* R4 band boundaries — makes the three bands readable off the dial itself. */}
        {[bandLow, bandHigh].map((boundary) => {
          const outer = polar(degreesForPct(boundary), RADIUS + TRACK_WIDTH / 2 - 1);
          const inner = polar(degreesForPct(boundary), RADIUS - TRACK_WIDTH / 2 + 1);
          return (
            <line
              key={boundary}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="var(--canvas)"
              strokeWidth={2}
            />
          );
        })}

        {hasValue && (
          <path
            d={arcPath(0, Math.max(clamped, 0.4), RADIUS)}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={TRACK_WIDTH}
            strokeLinecap="round"
          />
        )}

        {/* Scale ends, so the reader knows the range without a legend. */}
        <text
          x={polar(180, RADIUS).x}
          y={CY + 22}
          textAnchor="middle"
          className="fill-[var(--muted)] text-[11px]"
        >
          0%
        </text>
        <text
          x={polar(0, RADIUS).x}
          y={CY + 22}
          textAnchor="middle"
          className="fill-[var(--muted)] text-[11px]"
        >
          100%
        </text>

        {/* ── needle, drawn BEFORE the label so the label paints over it ── */}
        {hasValue && (
          <g>
            <polygon
              points={`${needleTip.x},${needleTip.y} ${needleBaseA.x},${needleBaseA.y} ${needleBaseB.x},${needleBaseB.y}`}
              fill="var(--text)"
              opacity={0.9}
            />
            <circle cx={CX} cy={CY} r={7} fill="var(--text)" />
            <circle cx={CX} cy={CY} r={3} fill="var(--surface)" />
          </g>
        )}

        {/* Backing plate: keeps the numeral legible where the needle passes under it. */}
        {hasValue && (
          <rect
            x={CX - 48}
            y={CY - 62}
            width={96}
            height={46}
            rx={12}
            fill="var(--surface)"
            opacity={0.94}
          />
        )}

        <text
          x={CX}
          y={CY - 28}
          textAnchor="middle"
          className="fill-[var(--text)] font-mono text-[34px] font-semibold"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {hasValue ? `${clamped}%` : '—'}
        </text>
      </svg>

      <figcaption className="mt-1 text-center">
        <p className="text-sm font-medium text-ink">{label}</p>
        {bandLabel && <p className="mt-0.5 text-xs text-muted">{bandLabel}</p>}
      </figcaption>
    </figure>
  );
}
